// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { getProjectBillingInfo } from '@/project/api/get-project-billing-info';
import { ProjectBillingInfo } from '@/project/api/types';
import {
  focusManager,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';

/**
 * Fetches the narrow project billing fields used by the invoice edit dialog
 * to coalesce customer fields against project overrides. Suspends on first
 * load, and re-suspends when the window regains focus so an edit made in
 * another tab shows up on return (the spinner renders again via the Suspense
 * boundary during the refetch).
 *
 * Why `resetQueries` and not `invalidateQueries`: `useSuspenseQuery` only
 * suspends when `data === undefined`. `invalidateQueries` marks the query
 * stale and refetches in the background while keeping the old data — no
 * re-suspend. `resetQueries` removes the data, which forces
 * `useSuspenseQuery` to throw its promise again, which the Suspense boundary
 * catches.
 *
 * When `projectKey` is null, the query resolves to null immediately — no
 * network call, but the hook still returns a value compatible with
 * `useSuspenseQuery`.
 */
export function useProjectBillingInfo(
  projectKey: string | null
): ProjectBillingInfo | null {
  const queryClient = useQueryClient();

  useEffect(() => {
    return focusManager.subscribe((focused) => {
      if (focused) {
        void queryClient.resetQueries({
          queryKey: ['project-billing-info', projectKey],
        });
      }
    });
  }, [queryClient, projectKey]);

  const { data } = useSuspenseQuery({
    queryKey: ['project-billing-info', projectKey],
    queryFn: () => {
      if (projectKey === null) return Promise.resolve(null);
      // Non-fatal: if the project billing fetch fails, the edit dialog
      // should stay usable. Coalesce degenerates to customer-only (all
      // project overrides treated as null). Report to Sentry so the
      // failure isn't silent. Using .catch() on the promise directly
      // (rather than await + try/catch) attaches the handler synchronously
      // so runtimes don't transiently flag the rejection as unhandled.
      return getProjectBillingInfo(projectKey)
        .then((response) => response.data)
        .catch((err: unknown) => {
          // Only report unexpected server failures (5xx). Client-side
          // failures (404 for a stale project key, 403, network drops)
          // are benign from this hook's perspective — coalesce falls back
          // to customer-only.
          const status = axios.isAxiosError(err)
            ? err.response?.status
            : undefined;
          if (status !== undefined && status >= 500) {
            Sentry.captureException(err, {
              tags: { hook: 'useProjectBillingInfo' },
            });
          }
          return null;
        });
    },
  });

  return data;
}
